import { clsx } from '@alexis/helpers/clsx';
import React, { forwardRef, type PropsWithChildren, useCallback } from 'react';

import CloseIcon from '@icons/new/circle-fill-x.svg';

import ClearIcon from '../icons/close.svg';
import styles from '../styles/components/InputSelector.module.scss';

interface InputSelectorProps {
  className?: string;
  hasError?: boolean;
  id?: string;
  isFlightInbound?: boolean;
  isFlightProduct?: boolean;
  isFocus: boolean;
  isValidated: boolean;
  isActive?: boolean;
  listContainerId?: string;
  name?: string;
  activeDescendant?: string;
  listAriaLabel?: string;
  inputAriaLabel?: string;
  onChange(value: string): void;
  onClear?(): void;
  onFocus(): void;
  onBlur?(): void;
  onKeyDown?(event: React.KeyboardEvent<HTMLInputElement>): void;
  placeholder: string;
  style?: React.CSSProperties;
  value?: string;
  dataPw?: string;
}

const InputSelector = forwardRef<HTMLInputElement, PropsWithChildren<InputSelectorProps>>(
  (
    {
      children,
      className,
      hasError,
      id,
      isFlightInbound,
      isFlightProduct,
      isFocus,
      isValidated,
      isActive,
      listContainerId,
      name,
      activeDescendant,
      listAriaLabel,
      inputAriaLabel,
      onChange,
      onClear,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      style,
      value,
      dataPw,
    },
    ref,
  ) => {
    const isShowingChildren = React.Children.toArray(children).some((c) => !!c) && isFocus;

    const handleInputClick = useCallback<(event: React.MouseEvent<HTMLInputElement>) => void>(
      (event: React.MouseEvent<HTMLInputElement>) => {
        if (isFocus) {
          event.stopPropagation();
        }
        onFocus();
      },
      [onFocus, isFocus],
    );

    const handleClearButtonClick = useCallback<
      (event: React.MouseEvent<HTMLButtonElement>) => void
    >(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        if (!!onClear) {
          onClear();
        }
      },
      [onClear],
    );

    const defaultInputId = 'inputId';

    return (
      <div
        data-testid='input-selector'
        className={clsx(
          styles.container,
          className,
          isFocus && styles.focus,
          isValidated && styles.validated,
          !!hasError && styles.error,
          isActive && styles.active,
        )}
        style={style}
      >
        <div className={clsx(styles.inputContainer, isFlightInbound && styles.flightInbound)}>
          <input
            autoComplete='off'
            id={id ?? defaultInputId}
            inputMode='text'
            name={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            onClick={handleInputClick}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            ref={ref}
            spellCheck={false}
            type='text'
            value={value}
            tabIndex={2}
            role='combobox'
            aria-autocomplete='list'
            aria-haspopup='true'
            aria-expanded={isShowingChildren}
            aria-controls={listContainerId}
            aria-activedescendant={isShowingChildren ? activeDescendant : undefined}
            aria-label={inputAriaLabel}
          />
          <label htmlFor={id ?? defaultInputId}>{placeholder}</label>

          {!!value && !!onClear && isFocus ? (
            <button
              type='button'
              aria-label='Clear input'
              className={styles.clearButton}
              onClick={handleClearButtonClick}
            >
              <ClearIcon className={styles.icon} />
            </button>
          ) : null}

          {isFlightProduct && value !== 'Anywhere' && !isFocus ? (
            <button
              type='button'
              aria-label='Clear input (hover)'
              className={clsx(styles.hoverClearButton, !isFlightInbound && styles.flightOutbound)}
              onClick={handleClearButtonClick}
            >
              <CloseIcon className={styles.icon} />
            </button>
          ) : null}

          {isFlightProduct ? <div className={styles.box}></div> : null}
        </div>

        {isFocus ? (
          <ul
            data-pw={dataPw}
            id={listContainerId}
            className={styles.children}
            onClick={(e) => e.stopPropagation()}
            role='listbox'
            aria-label={listAriaLabel}
          >
            {children}
          </ul>
        ) : null}
      </div>
    );
  },
);

export default InputSelector;
